import { z } from 'zod';

import { cardReferenceRegionSchema } from '../card-reference.ts';
import { deckScopeSchema, type DeckScope } from '../deck.ts';
import { submittedAnswerSchema } from '../review-event.ts';
import { responseIdSchema } from './common.ts';

const cardOptionSchema = z
  .object({ id: responseIdSchema, text: z.string().min(1) })
  .strict();
export const studyCardSchema = z.discriminatedUnion('type', [
  z
    .object({
      id: responseIdSchema,
      items: z
        .array(
          z
            .object({
              sourceIndex: z.number().int().nonnegative(),
              text: z.string().min(1),
            })
            .strict(),
        )
        .min(2)
        .max(9),
      prompt: z.string().min(1),
      type: z.literal('order-words'),
    })
    .strict(),
  z
    .object({
      id: responseIdSchema,
      prompt: z.string().min(1),
      type: z.literal('text-input'),
    })
    .strict(),
  z
    .object({
      id: responseIdSchema,
      options: z.array(cardOptionSchema).min(2),
      prompt: z.string().min(1),
      type: z.literal('choice'),
    })
    .strict(),
  z
    .object({
      id: responseIdSchema,
      options: z.array(cardOptionSchema).min(3),
      prompt: z.string().min(1),
      type: z.literal('multi-choice'),
    })
    .strict(),
]);

const feedbackReferenceSchema = z
  .object({
    deckId: responseIdSchema,
    hasLocalCopy: z.boolean(),
    page: z.number().int().positive(),
    referenceDocumentId: responseIdSchema,
    region: cardReferenceRegionSchema.nullish(),
    title: z.string(),
  })
  .strict();
export const studyFeedbackSchema = z
  .object({
    correctAnswer: z.string().optional(),
    correctOptions: z.array(cardOptionSchema).optional(),
    correctOrder: z.array(z.number().int().nonnegative()).optional(),
    reference: feedbackReferenceSchema.optional(),
    result: z.enum(['correct', 'incorrect']),
  })
  .strict();
export const studyNextSchema = z
  .object({ card: studyCardSchema.nullable() })
  .strict();
export const studyAnswerSchema = z
  .object({
    answer: submittedAnswerSchema,
    cardId: responseIdSchema,
    scope: deckScopeSchema.optional(),
  })
  .strict();
export const studyAnswerResponseSchema = z
  .object({ feedback: studyFeedbackSchema, next: studyCardSchema.nullable() })
  .strict();

export type StudyCard = z.infer<typeof studyCardSchema>;
export type StudyNextResponse = z.infer<typeof studyNextSchema>;
export type StudyAnswer = z.infer<typeof submittedAnswerSchema>;
export type StudyFeedback = z.infer<typeof studyFeedbackSchema>;
export type StudyAnswerResponse = z.infer<typeof studyAnswerResponseSchema>;
export type { DeckScope } from '../deck.ts';
